// SINGLE SOURCE for the CCIS email design used by every email the website sends (staff notifications and the
// client quotation email). Same Outlook-safe teal layout as the Supabase email functions: teal header band,
// bold title, short message, optional details box, optional teal button, CCIS footer.
//
// The send-internal-email / send-client-email functions ALSO wrap any plain (non-document) html in this design
// as a safety net — so nothing can ever go out unstyled — but building it here gives the nicer details box/button.

const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const FIRM = () => { try { return localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd' } catch (e) { return 'CCIS Singapore Pte Ltd' } }

// 15 Jan 2027 — never raw ISO dates in an email.
export function emailDate(d) {
  if (!d) return '—'
  const s = String(d).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return String(d)
  const [y, m, day] = s.split('-').map(Number)
  return `${day} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}`
}

function button(label, url) {
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:6px 0 4px;"><tr>
    <td bgcolor="#006363" style="background:#006363;border-radius:6px;">
      <a href="${esc(url)}" style="display:inline-block;padding:10px 28px;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;">${esc(label)}</a>
    </td></tr></table>`
}

function shell(title, innerHtml) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head>
<body bgcolor="#f4f5f3" style="margin:0;padding:0;background:#f4f5f3;font-family:Arial,Helvetica,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f3;"><tr><td align="center" style="padding:28px 12px;">
  <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;border:1px solid #e3e5e2;">
    <tr><td bgcolor="#006363" style="background:#006363;padding:18px 32px;">
      <div style="font-size:14px;font-weight:700;color:#ffffff;letter-spacing:1.5px;">CCIS SINGAPORE PTE LTD</div>
      <div style="font-size:10px;color:#a9d6d6;letter-spacing:1px;margin-top:3px;">FOR A BRIGHTER BUSINESS TOMORROW</div>
    </td></tr>
    <tr><td style="padding:28px 32px 14px;color:#2b2b2b;font-size:14px;line-height:1.7;">
      ${title ? `<div style="font-size:16px;font-weight:700;color:#0e3b3b;margin:0 0 16px;">${esc(title)}</div>` : ''}
      ${innerHtml}
    </td></tr>
    <tr><td style="padding:14px 32px 20px;border-top:1px solid #ececea;">
      <div style="font-size:11px;color:#9a9a9a;line-height:1.55;">${esc(FIRM())} &middot; Reg. No. 199205611D<br>16 Jalan Kilang Timor #08-03, Redhill Forum Singapore 159308</div>
      <div style="font-size:11px;color:#b0b0b0;line-height:1.55;margin-top:10px;">This is an automated message from AuditPM.</div>
    </td></tr>
  </table>
</td></tr></table>
</body></html>`
}

const para = html => `<p style="margin:0 0 14px;font-size:14px;color:#444;line-height:1.7;">${html}</p>`

// Structured email.
//   title    — bold heading under the teal band
//   greeting — e.g. "Dear Accounts," (plain text; may contain {{staff_name}}, filled in by send-internal-email)
//   lines    — array of paragraphs; plain text is escaped, use { html } for trusted markup (e.g. <strong>)
//   details  — [[label, value], …] shown in a neat box (plain text values)
//   button   — { label, url }
//   note     — small grey line after the button
export function emailHtml({ title, greeting, lines = [], details = [], button: btn, note }) {
  const body = [
    greeting ? para(esc(greeting)) : '',
    ...lines.filter(Boolean).map(l => para(typeof l === 'object' ? l.html : esc(l))),
    details.length ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e3e5e2;border-collapse:collapse;margin:0 0 18px;">
      ${details.filter(Boolean).map(([k, v]) => `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #ececea;font-size:13px;color:#777;width:38%;vertical-align:top;">${esc(k)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #ececea;font-size:13px;color:#2b2b2b;font-weight:700;">${esc(v)}</td></tr>`).join('')}
    </table>` : '',
    btn?.url ? button(btn.label || 'Open →', btn.url) : '',
    note ? `<p style="margin:14px 0 0;font-size:13px;color:#777;line-height:1.6;">${esc(note)}</p>` : '',
  ].join('')
  return shell(title, body)
}

// Template email (text editable under Settings). Each line becomes a paragraph; a line that is ONLY a link becomes
// a teal button; other links stay clickable.
export function textEmailHtml({ title, text, buttonLabel = 'Open →' }) {
  const linkify = s => s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color:#006363;">$1</a>')
  const out = []
  for (const block of String(text || '').split(/\n\s*\n/)) {        // blank line = new paragraph
    let lines = []
    const flush = () => { if (lines.length) out.push(para(lines.map(l => linkify(esc(l))).join('<br>'))); lines = [] }
    for (const raw of block.split('\n')) {
      const l = raw.trim()
      if (/^https?:\/\/\S+$/.test(l)) { flush(); out.push(button(buttonLabel, l)) }   // a link on its own line → button
      else if (l) lines.push(raw.replace(/\s+$/, ''))
    }
    flush()
  }
  return shell(title, out.join(''))
}
